import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { getSupabase } from './supabaseClient';
import { StudentProfile, SupervisorProfile } from './database.types';

// Use the existing types from database.types.ts
type UserProfile = StudentProfile | SupervisorProfile;

interface AuthContextType {
  session: Session | null;
  user: UserProfile | null;
  userType: 'student' | 'supervisor' | 'ojt_coordinator' | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  userType: null,
  loading: true,
  signOut: async () => {},
  refreshSession: async () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [userType, setUserType] = useState<'student' | 'supervisor' | 'ojt_coordinator' | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  
  // Track the last fetched user ID to avoid unnecessary refetches
  const [lastFetchedUserId, setLastFetchedUserId] = useState<string | null>(null);
  
  // Stabilize user object to prevent infinite re-renders
  const [userStable, setUserStable] = useState<UserProfile | null>(null);
  
  // Prevent multiple initializations
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Stabilize user object - only update if user ID or essential properties change
  useEffect(() => {
    if (user && user.id) {
      setUserStable(prevUser => {
        // Only update if user ID changed or essential properties changed
        if (!prevUser || prevUser.id !== user.id) {
          return user;
        }
        return prevUser;
      });
    } else {
      setUserStable(null);
    }
  }, [user?.id, user?.first_name, user?.last_name, (user as any)?.role, (user as any)?.user_role]);

  /**
   * Fetch profile from database - only called when session user ID changes
   * This prevents unnecessary API calls during token refreshes
   */
  const fetchProfileFromDB = useCallback(async (userId: string): Promise<UserProfile | null> => {
    // Skip if we already fetched this user
    if (lastFetchedUserId === userId) {
      return user;
    }

    console.log('Fetching profile from database for user:', userId);
    const supabase = getSupabase();
    
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
      );

      // Fetch both student and supervisor profiles in parallel with timeout
      const profilePromise = Promise.all([
        supabase.from('student_profile').select('*').eq('id', userId).maybeSingle(),
        supabase.from('supervisor_profiles').select('*').eq('id', userId).maybeSingle()
      ]);

      const [studentResult, supervisorResult] = await Promise.race([profilePromise, timeoutPromise]) as any;

      const studentProfile = studentResult.data;
      const supervisorProfile = supervisorResult.data;

      // Handle errors (ignore PGRST116 which means "no rows returned")
      if (studentResult.error && studentResult.error.code !== 'PGRST116') {
        console.error('Error fetching student profile:', studentResult.error);
      }
      if (supervisorResult.error && supervisorResult.error.code !== 'PGRST116') {
        console.error('Error fetching supervisor profile:', supervisorResult.error);
      }

      // Determine which profile to use
      let selectedProfile: UserProfile | null = null;
      let selectedUserType: 'student' | 'supervisor' | 'ojt_coordinator' | null = null;

      if (studentProfile && supervisorProfile) {
        // User has both profiles - prefer last known role or current route context
        const currentPath = window.location.pathname;
        const isSupervisorContext = currentPath.includes('/supervisor') || 
                                  currentPath.includes('/ojt-coordinator') ||
                                  currentPath.includes('/trainees');
        const lastRole = localStorage.getItem('lastUserType');

        if (lastRole === 'supervisor' || lastRole === 'ojt_coordinator' || isSupervisorContext) {
          selectedProfile = supervisorProfile;
          selectedUserType = (supervisorProfile as SupervisorProfile).user_role === 'ojt_coordinator' ? 'ojt_coordinator' : 'supervisor';
        } else {
          selectedProfile = studentProfile;
          selectedUserType = 'student';
        }
      } else if (studentProfile) {
        selectedProfile = studentProfile;
        selectedUserType = 'student';
      } else if (supervisorProfile) {
        selectedProfile = supervisorProfile;
        selectedUserType = (supervisorProfile as SupervisorProfile).user_role === 'ojt_coordinator' ? 'ojt_coordinator' : 'supervisor';
      } else {
        // No profile found - create minimal student profile
        console.warn('No profile found for user:', userId);
        selectedProfile = {
          id: userId,
          email: '',
          first_name: '',
          last_name: '',
          student_id: '',
          course_info: '',
          department: '',
          supervisor: '',
          role: 'student' as const
        };
        selectedUserType = 'student';
      }

      // Cache the profile for future use (as placeholder only)
      if (selectedProfile) {
        const profileWithTimestamp = {
          ...selectedProfile,
          cachedAt: new Date().toISOString()
        };
        localStorage.setItem('userProfile', JSON.stringify(profileWithTimestamp));
      }

      // Update state
      setUser(selectedProfile);
      setUserType(selectedUserType);
      try { localStorage.setItem('lastUserType', selectedUserType || ''); } catch {}
      setLastFetchedUserId(userId);

      return selectedProfile;
    } catch (error) {
      console.error('Error fetching profile from database:', error);

      // Create role-aware fallback profile to prevent redirects on refresh
      let fallbackUserType: 'student' | 'supervisor' | 'ojt_coordinator' = 'student';
      try {
        const lastRole = (localStorage.getItem('lastUserType') || '') as any;
        const currentPath = window.location.pathname;
        const isSupervisorContext = currentPath.includes('/supervisor') ||
                                    currentPath.includes('/ojt-coordinator') ||
                                    currentPath.includes('/trainees');
        if (lastRole === 'supervisor' || lastRole === 'ojt_coordinator' || isSupervisorContext) {
          fallbackUserType = lastRole === 'ojt_coordinator' ? 'ojt_coordinator' : 'supervisor';
        }
      } catch {}

      if (fallbackUserType === 'student') {
        const fallbackProfile: StudentProfile = {
          id: userId,
          email: '',
          first_name: '',
          last_name: '',
          student_id: '',
          course_info: '',
          department: '',
          supervisor: '',
          role: 'student' as const
        };
        setUser(fallbackProfile);
        setUserType('student');
        setLastFetchedUserId(userId);
        return fallbackProfile;
      } else {
        const fallbackProfile: SupervisorProfile = {
          id: userId,
          email: '',
          first_name: '',
          last_name: '',
          office: '',
          role_title: '',
          user_role: fallbackUserType === 'ojt_coordinator' ? 'ojt_coordinator' : 'supervisor'
        } as SupervisorProfile;
        setUser(fallbackProfile);
        setUserType(fallbackUserType);
        setLastFetchedUserId(userId);
        return fallbackProfile as unknown as UserProfile;
      }
    }
  }, [user, lastFetchedUserId]);

  /**
   * Load cached profile as placeholder while session is being verified
   * This prevents flicker by showing cached data immediately
   */
  const loadCachedProfile = useCallback(() => {
    try {
      const cachedProfile = localStorage.getItem('userProfile');
      if (cachedProfile) {
        const parsed = JSON.parse(cachedProfile);
        if (parsed && parsed.id) {
          // Determine current route context (student vs supervisor/coordinator)
          const currentPath = window.location.pathname;
          const isSupervisorContext = currentPath.includes('/supervisor') ||
                                      currentPath.includes('/ojt-coordinator') ||
                                      currentPath.includes('/trainees');

          // Determine user type from cached profile
          const cachedUserType = parsed.role === 'student' ? 'student' :
                               (parsed as any).user_role === 'ojt_coordinator' ? 'ojt_coordinator' : 'supervisor';

          // IMPORTANT: If we're in a supervisor context but the cached profile is a student,
          // skip using the cached profile to avoid incorrect auto sign-outs/navigation.
          if (isSupervisorContext && cachedUserType === 'student') {
            console.log('Skipping cached student profile in supervisor context');
            return null;
          }

          console.log('Loading cached profile as placeholder');
          setUser(parsed);
          setUserType(cachedUserType);
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading cached profile:', error);
    }
    return null;
  }, []);

  /**
   * Clear all auth state and cached data
   */
  const clearAuthState = useCallback(() => {
    console.log('Clearing auth state');
    setSession(null);
    setUser(null);
    setUserType(null);
    setLastFetchedUserId(null);
    localStorage.removeItem('userProfile');
  }, []);

  /**
   * Sign out function
   */
  const signOut = useCallback(async () => {
    try {
      console.log('Starting sign out process');
      setLoading(true);
      
      // Clear local state immediately
      clearAuthState();
      
      // Sign out from Supabase
      const supabase = getSupabase();
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('Error signing out from Supabase:', error);
        // Don't throw - we've already cleared local state
      } else {
        console.log('Successfully signed out from Supabase');
      }
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      setLoading(false);
    }
  }, [clearAuthState]);

  /**
   * Manual session refresh function
   */
  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      console.log('Manually refreshing session');
      const supabase = getSupabase();
      const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession();
      
      if (error || !refreshedSession) {
        console.error('Error refreshing session:', error);
        return false;
      }
      
      console.log('Session refreshed successfully');
      setSession(refreshedSession);
      
      // Only fetch profile if user ID changed
      if (refreshedSession.user && refreshedSession.user.id !== lastFetchedUserId) {
        await fetchProfileFromDB(refreshedSession.user.id);
      }
      
      return true;
    } catch (error) {
      console.error('Error in manual session refresh:', error);
      return false;
    }
  }, [fetchProfileFromDB, lastFetchedUserId]);

  useEffect(() => {
    const supabase = getSupabase();
    let mounted = true;

    const initializeAuth = async () => {
      // Prevent multiple initializations
      if (hasInitialized) {
        console.log('Auth already initialized, skipping...');
        return;
      }
      
      console.log('Starting auth initialization...');
      setHasInitialized(true);
      
      try {
        setInitializing(true);
        
        // Load cached profile immediately to prevent flicker
        const cachedProfile = loadCachedProfile();
        console.log('Cached profile loaded:', !!cachedProfile);
        
        // If we have a cached profile and it's appropriate for this route, use it immediately
        if (cachedProfile) {
          console.log('Using cached profile immediately - skipping session verification');
          
          // Try to get session quickly, but don't wait for it
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user && session.user.id === cachedProfile.id) {
              console.log('Session verified for cached profile');
              setSession(session);
            } else {
              console.log('Session mismatch or missing, using dummy session');
              setSession({} as any); // Dummy session for cached profile
            }
          }).catch((error) => {
            console.warn('Session fetch failed, using dummy session:', error);
            setSession({} as any);
          });
          
          // Complete initialization immediately with cached data
          console.log('Auth initialization complete with cached profile');
          setInitializing(false);
          setLoading(false);
          return;
        }
        
        // Only do complex initialization if no cached profile
        console.log('No cached profile, doing full initialization...');
        
        // AGGRESSIVE TIMEOUT - Force completion after 3 seconds max
        const initTimeout = setTimeout(() => {
          console.warn('Auth initialization timeout (3s), forcing completion');
          setInitializing(false);
          setLoading(false);
        }, 3000);
        
        // Get initial session with timeout
        const sessionPromise = supabase.auth.getSession();
        const sessionTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session fetch timeout')), 2000)
        );
        
        let initialSession: any = null;
        try {
          const { data: { session }, error } = await Promise.race([sessionPromise, sessionTimeout]) as any;
          
          if (!mounted) {
            clearTimeout(initTimeout);
            return;
          }

          if (error) {
            console.error('Error getting initial session:', error);
            clearAuthState();
            return;
          }

          initialSession = session;
        } catch (sessionError) {
          console.warn('Session fetch timeout, proceeding with placeholder session');
          // Use a placeholder session to avoid immediate redirects on protected routes
          setSession({} as any);
          // Continue initialization; profile fetch may still succeed later via auth listener
          // Do not clear auth state here
          // Intentionally not returning to allow downstream logic to run
        }

        if (initialSession?.user) {
          console.log('Initial session found, setting session state');
          setSession(initialSession);
          
          // Try to fetch fresh profile, but with very short timeout
          try {
            console.log('Attempting to fetch fresh profile...');
            await Promise.race([
              fetchProfileFromDB(initialSession.user.id),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Profile fetch timeout')), 1500)
              )
            ]);
            console.log('Fresh profile fetched successfully');
          } catch (profileError) {
            console.warn('Profile fetch failed, creating minimal profile:', profileError);
            // Create minimal profile immediately
            const minimalProfile: StudentProfile = {
              id: initialSession.user.id,
              email: initialSession.user.email || '',
              first_name: '',
              last_name: '',
              student_id: '',
              course_info: '',
              department: '',
              supervisor: '',
              role: 'student' as const
            };
            setUser(minimalProfile);
            setUserType('student');
            setLastFetchedUserId(initialSession.user.id);
          }
        } else {
          console.log('No initial session found');
          // Avoid aggressive clear that causes immediate redirect on refresh.
          // Keep state as-is; the app can still render public parts or await a later auth event.
        }
        
        clearTimeout(initTimeout);
      } catch (error) {
        console.error('Error during auth initialization:', error);
        clearAuthState();
      } finally {
        console.log('Auth initialization complete, setting loading to false');
        setInitializing(false);
        setLoading(false);
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      console.log('Auth state changed:', { event, userId: session?.user?.id });

      /**
       * Handle INITIAL_SESSION specially to avoid interference with initialization
       * This event is fired when Supabase first loads the session from storage,
       * but we handle initial session loading in initializeAuth() above
       */
      if (event === 'INITIAL_SESSION') {
        console.log('INITIAL_SESSION event received - handled by initializeAuth');
        return;
      }

      // Handle SIGNED_IN events
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('User signed in, updating session and fetching profile');
        setSession(session);
        
        // Only fetch profile if user ID changed
        if (session.user.id !== lastFetchedUserId) {
          await fetchProfileFromDB(session.user.id);
        }
        return;
      }

      // Handle SIGNED_OUT events
      if (event === 'SIGNED_OUT') {
        console.log('User signed out, clearing auth state');
        clearAuthState();
        return;
      }

      // Handle TOKEN_REFRESHED events (Supabase auto-refresh)
      if (event === 'TOKEN_REFRESHED' && session?.user) {
        console.log('Token refreshed, updating session');
        setSession(session);
        
        // Don't refetch profile on token refresh - user data hasn't changed
        return;
      }
    });

    // Initialize auth state
    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [hasInitialized]); // FIXED: Only depend on hasInitialized

  // Combine loading states - show loading while initializing OR when explicitly loading
  const isLoading = initializing || loading;
  
  // Debug logging for loading states
  useEffect(() => {
    console.log('AuthProvider loading state changed:', {
      initializing,
      loading,
      isLoading,
      hasSession: !!session,
      hasUser: !!user,
      userType,
      timestamp: new Date().toISOString()
    });
  }, [initializing, loading, isLoading, session, user, userType]);

  // EMERGENCY timeout to prevent infinite loading (very aggressive)
  useEffect(() => {
    if (isLoading) {
      const emergencyTimeout = setTimeout(() => {
        console.warn('EMERGENCY OVERRIDE: Auth loading timeout - forcing completion');
        console.warn('Current state:', { initializing, loading, hasSession: !!session, hasUser: !!user });
        
        // FORCE all loading states to false
        setInitializing(false);
        setLoading(false);
        
        // If we have a session but no user, create a minimal user immediately
        if (session?.user && !user) {
          console.warn('Creating emergency minimal user profile');
          const emergencyProfile: StudentProfile = {
            id: session.user.id,
            email: session.user.email || '',
            first_name: '',
            last_name: '',
            student_id: '',
            course_info: '',
            department: '',
            supervisor: '',
            role: 'student' as const
          };
          setUser(emergencyProfile);
          setUserType('student');
          setLastFetchedUserId(session.user.id);
        }
        
        // If no session, clear everything
        if (!session) {
          console.warn('Clearing auth state due to emergency timeout');
          clearAuthState();
        }
      }, 8000); // 8 second emergency timeout (very aggressive)

      return () => clearTimeout(emergencyTimeout);
    }
  }, [isLoading, initializing, loading, session, user]);

  // Add debug function to global window for manual override
  useEffect(() => {
    (window as any).forceAuthComplete = () => {
      console.warn('🔧 MANUAL OVERRIDE: Forcing auth completion from console');
      setInitializing(false);
      setLoading(false);
      
      if (session?.user && !user) {
        const emergencyProfile: StudentProfile = {
          id: session.user.id,
          email: session.user.email || '',
          first_name: '',
          last_name: '',
          student_id: '',
          course_info: '',
          department: '',
          supervisor: '',
          role: 'student' as const
        };
        setUser(emergencyProfile);
        setUserType('student');
        setLastFetchedUserId(session.user.id);
        console.log('Manual override: Created minimal user profile');
      } else {
        clearAuthState();
        console.log('Manual override: Cleared auth state');
      }
    };
    
        console.log('Debug function available: window.forceAuthComplete()');
    
    return () => {
      delete (window as any).forceAuthComplete;
    };
  }, [session, user, clearAuthState]);

  return (
    <AuthContext.Provider value={{ 
      session, 
      user: userStable, 
      userType, 
      loading: isLoading, 
      signOut, 
      refreshSession 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Main authentication hook
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Student-specific authentication hook
 */
export const useStudentAuth = () => {
  const auth = useAuth();
  return {
    session: auth.session,
    student: auth.user as StudentProfile | null,
    userType: auth.userType,
    loading: auth.loading,
    signOut: auth.signOut,
    refreshSession: auth.refreshSession,
  };
};

/**
 * Supervisor-specific authentication hook with context awareness
 */
export const useSupervisorAuth = () => {
  const auth = useAuth();
  const [isSupervisorContext, setIsSupervisorContext] = useState(false);
  
  useEffect(() => {
    const currentPath = window.location.pathname;
    const supervisorPaths = ['/supervisor', '/ojt-coordinator', '/trainees'];
    const isSupervisorPage = supervisorPaths.some(path => currentPath.includes(path));
    setIsSupervisorContext(isSupervisorPage);
  }, []);
  
  return {
    session: auth.session,
    supervisor: auth.user as SupervisorProfile | null,
    userType: auth.userType,
    loading: auth.loading,
    signOut: auth.signOut,
    refreshSession: auth.refreshSession,
    isSupervisorContext,
    isAuthorized: auth.userType === 'supervisor' || auth.userType === 'ojt_coordinator' || isSupervisorContext,
  };
};
